import { FC, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { AsH1, HeadingSM } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { EmbeddedVideo } from '@/molecules/EmbeddedVideo'
import { SimpleThumbnail } from '@/molecules/Thumbnail'
import { TheatricalReleaseResult } from '@/services/Theatrical/queries'
import { Faq } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { mergeQueryParameters } from '@/utils/UriBuilder'
import { useSafeTrack } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { BackToTopButton } from '@/views/PayItForward/TheatricalPayItForwardView/components/BackToTopButton'
import { TheatricalPifFaq } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifFaq'
import { TheatricalPifGoalLine } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifGoalLine'
import { TheatricalPifHowYouCanHelpSection } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifHowYouCanHelpSection'
import { PosterOrTrailer } from '@/views/TicketCheckoutViews/ShowtimesView/components/PosterOrTrailer'
import { PifSectionTitle } from './components/PifSectionTitle'

export interface ProjectPIFData {
  name: string
  slug: string
  pifExplainerVideoUrl: string
  pifExplainerText: string
  logoCloudinaryPath: string
}

interface ViewProps {
  theatricalRelease: TheatricalReleaseResult
  newPifLayout?: boolean
  displayNumberOfTickets?: boolean
  pifNameInCreditsEnabled?: boolean
}
const davidTrailerUrl =
  'https://www.youtube-nocookie.com/embed/2Dolwo1UNbA?autoplay=1&playsinline=1&rel=0&modestbranding=1'
const davidTrailerThumbnail = 'https://images.angelstudios.com/image/upload/v1760658989/david/frame_5_play.webp'
// David trailer experiment variations
export const TheatricalPayItForwardView: FC<ViewProps> = ({
  theatricalRelease,
  newPifLayout,
  displayNumberOfTickets,
}) => {
  const track = useSafeTrack()
  const { locale } = useLocale()
  const lastViewTracked = useRef<string>()
  const [loadPlayer, setLoadPlayer] = useState(false)
  const { query } = useSafeRouter()
  const isPifDefaultedOpen = query?.defaultOpen === 'true'
  const [shouldShowPifSection, setShouldShowPifSection] = useState(isPifDefaultedOpen)
  const { t } = useTranslate('pif')

  const isDavidProject = theatricalRelease?.theatricalSlug === slugs.david

  // Default trailer URL/thumbnail for non-David projects
  const trailerUrl = useMemo(() => {
    if (!theatricalRelease?.region?.trailerUrl) return ''
    return mergeQueryParameters(theatricalRelease?.region?.trailerUrl, { autoplay: '1' })
  }, [theatricalRelease?.region?.trailerUrl])

  const trailerThumbnail = theatricalRelease?.region?.trailerThumbnail ?? ''

  useEffect(() => {
    if (isPifDefaultedOpen) {
      setShouldShowPifSection(true)
    }
  }, [isPifDefaultedOpen])

  const isSpanish = locale === 'es'

  const handleLoadPlayer = () => {
    setLoadPlayer(!loadPlayer)
  }

  useEffect(() => {
    if (lastViewTracked.current === theatricalRelease?.theatricalSlug) return

    track('PIF Project Page Viewed', {
      projectSlug: theatricalRelease?.theatricalSlug,
      projectName: theatricalRelease?.title,
    })
    lastViewTracked.current = theatricalRelease?.theatricalSlug
  }, [track, theatricalRelease?.theatricalSlug, theatricalRelease?.title])

  return (
    <PaddedContainer className="w-full !px-0 py-16 sm:!px-4 sm:py-20 md:!px-6 lg:!px-4 lg:py-28 xl:!px-3">
      {/* DESKTOP */}
      <div className="w-full lg:flex lg:gap-10 xl:gap-16">
        <div className="col-span-12 lg:w-[65%]">
          <header className="mb-6 hidden sm:block">
            {newPifLayout ? (
              <PifSectionTitle>
                {theatricalRelease?.title}
                <span>: {t('payItForward', 'Pay it Forward')}</span>
              </PifSectionTitle>
            ) : (
              <HeadingSM as={AsH1} className="lg:photon-heading-xl flex flex-wrap gap-x-2 gap-y-1" weight="bold">
                {theatricalRelease?.title}
                <span>: {t('payItForward', 'Pay it Forward')}</span>
              </HeadingSM>
            )}
          </header>
          <section id="pay-it-forward-explainer-video" className="mb-6 lg:mb-16">
            <When condition={isDavidProject}>
              <When condition={!loadPlayer}>
                <SimpleThumbnail
                  imageUrl={davidTrailerThumbnail}
                  onClick={handleLoadPlayer}
                  className="lg:rounded-xl"
                  showButton
                />
              </When>
              <When condition={loadPlayer}>
                <div className="lg:overflow-hidden lg:rounded-xl">
                  <EmbeddedVideo title="Trailer" url={davidTrailerUrl} />
                </div>
              </When>
            </When>

            {/* Default trailer for non-David projects */}
            <When condition={!isDavidProject}>
              <When condition={theatricalRelease?.region?.trailerUrl && !loadPlayer && trailerThumbnail}>
                <SimpleThumbnail
                  imageUrl={trailerThumbnail}
                  onClick={handleLoadPlayer}
                  className="lg:rounded-xl"
                  showButton
                />
              </When>
              <When condition={!isSpanish && loadPlayer}>
                <div className="lg:overflow-hidden lg:rounded-xl">
                  <EmbeddedVideo title="Trailer" url={trailerUrl} />
                </div>
              </When>
              <When condition={isSpanish && loadPlayer}>
                <div className="lg:overflow-hidden lg:rounded-xl lg:border-transparent">
                  <PosterOrTrailer posterUrl={theatricalRelease?.horizontalPoster ?? ''} youtubeUrl={trailerUrl} />
                </div>
              </When>
              {!trailerThumbnail && theatricalRelease?.region?.trailerUrl && (
                <div className="lg:overflow-hidden lg:rounded-xl">
                  <EmbeddedVideo title="Trailer" url={theatricalRelease?.region?.trailerUrl} />
                </div>
              )}
            </When>
            <TheatricalPifGoalLine
              newPifLayout={newPifLayout}
              theatricalRelease={theatricalRelease}
              shouldUseIncrementer
              className="py-4 md:hidden"
            />
          </section>

          <div className="hidden md:block">
            <TheatricalPifGoalLine
              newPifLayout={newPifLayout}
              theatricalRelease={theatricalRelease}
              shouldUseIncrementer
              className={classNames(newPifLayout ? 'pt-[48px] pb-[32px]' : 'py-8')}
            />
          </div>

          <TheatricalPifFaq
            newPifLayout={newPifLayout}
            faqs={theatricalRelease?.region?.faqs as Faq[]}
            className="hidden lg:block"
          />
        </div>
        <div className="px-4 sm:px-0 lg:block lg:w-[35%] lg:min-w-[400px]">
          <TheatricalPifHowYouCanHelpSection
            newPifLayout={newPifLayout}
            theatricalRelease={theatricalRelease}
            shouldShowPifSection={shouldShowPifSection}
            setShouldShowPifSection={setShouldShowPifSection}
            displayNumberOfTickets={displayNumberOfTickets}
            pifNameInCreditsEnabled={false}
          />
          <TheatricalPifFaq
            newPifLayout={newPifLayout}
            faqs={theatricalRelease?.region?.faqs as Faq[]}
            className="mt-16 lg:hidden"
          />
        </div>
      </div>

      <BackToTopButton />
    </PaddedContainer>
  )
}
